<template>
  <div
    id="todo-list"
    class="page page-data-management"
  >
    <div class="page-toolbar bg-white shadow-sm p-3 justify-content-between align-items-center">
      <h4 class="m-0">
        To Do List
      </h4>
    </div>
    <div
      id="pageContent"
      class="page-content d-flex flex-column p-3"
    >
      <content-spinner :show="loaded" />
      <div
        v-show="!loaded"
        class="complex-table-view-wrapper"
      >
        <complex-table-view
          :small="true"
          :items="items"
          :is-todo="true"
          :fields="todoListFields"
          :todo-user-list="todoListData.userData"
          :total-rows="items.length"
          :modal-name="'todo-list'"
          @on-link="onLinkClick"
          @on-edit="onEditClick"
          @on-confirm="onConfirm"
          @filter="filterData"
          @sort-changed="onSortChanged"
          @context-change="contextChanged"
          @on-user-selected="onUserSelected"
          @update-todo-list-data="updateTodoListData"
          @on-history-click="getRecordHistory"
          @clear-selected-user="clearSelectedUser"
        />
      </div>
    </div>
    <view-row-modal />
  </div>
</template>

<script>
import ContentSpinner from '../../core/components/ContentSpinner';
import filterMixin from '../../core/mixins/filter.mixin';
import {createNamespacedHelpers} from 'vuex';
import ComplexTableView from '../../core/components/complex-table/components/ComplexTableView';
import ViewRowModal from '../../core/components/complex-table/ViewRowModal';

const {mapState, mapActions, mapGetters} = createNamespacedHelpers('dataManagement');
const {mapState: sidebarMapState} = createNamespacedHelpers('sidebar');

export default {
  name: 'TodoList',
  components: {
    ComplexTableView,
    ContentSpinner,
    ViewRowModal,
  },
  mixins: [filterMixin],
  data() {
    return {
      todoListFields: [
        {key: 'entityName', label: 'Entity', dataTypeName: 'text'},
        {key: 'recordName', label: 'Record Name', dataTypeName: 'text'},
        {key: 'dateLoaded', label: 'Date Loaded', dataTypeName: 'dateTime'},
        {key: 'validationStatus', label: 'Validation Status', dataTypeName: 'text'},
        {key: 'actionsRequired', label: 'Actions Required ', dataTypeName: 'text'},
      ].map(f => {
        f.name = f.key;
        return f;
      }),
      items: [],
      loading: false,
      filters: {},
      sortedParams: {
        sortBy: null,
        sortDesc: null,
      },
    };
  },
  watch: {
    ['todoListData.data']() {
      this.items = [...this.todoListData.data];

      if (Object.keys(this.filters).length > 0) {
        this.filterData(this.filters);
      }

      if (this.sortedParams.sortBy !== null) {
        this.onSortChanged(this.sortedParams);
      }
    },
  },
  computed: {
    ...sidebarMapState({
      menuItems: state => state.tablesList,
    }),
    ...mapState({
      todoListData: state => state.todoList.table,
    }),
    ...mapState(['validation']),
    ...mapGetters(['fields']),
    loaded() {
      return this.todoListData.loading || this.loading;
    },
  },
  methods: {
    ...mapActions([
      'showAddNewRecordModal',
      'getSingleRecordData',
      'getTable',
      'changePaginationData',
      'getTodoListData',
      'addNewRecord',
      'getTodoUserList',
      'getTodoListByUser',
      'showViewRecordModal',
      'getTable',
      'getTableFields',
    ]),
    onLinkClick(item) {
      const queryParams = {};
      const key = this.fieldValues.recordStatus;
      queryParams[key] = {
        fieldType: key,
        label: 'Record Status',
        selectedStatus: [item.validationStatus],
        type: '=',
      };
      const table = this.menuItems[item.entityGuid];
      this.$router.push({
        name: 'dataManagementTableView',
        params: {tableId: table.entityGuid, moduleUuid: table.moduleGuid},
        query: {moduleUuid: table.moduleGuid, filters: JSON.stringify(queryParams)},
      });
    },
    async onConfirm(item, selectedUser) {
      const success = await this.$confirm('Confirmed Row', 'Would you like to change \'Validated\' row to \'Confirmed\'?');
      if (success) {
        this.loading = true;
        const record = await this.getTableData(item);

        const data = {...{}, ...record};

        const params = {
          data: [this.formatPostData(data)],
          tableId: this.menuItems[item.entityGuid].entityGuid,
        };

        const lastUpdateDateName = this.fields.find(f => f.dataTypeName === 'lastUpdatedDate').name;
        params.data[0]['UploadAsAtDate'] = record[lastUpdateDateName];

        const validate = this.validation;
        if (record[validate.status] === validate.validated) {
          params.data[0][validate.fieldName] = validate.confirmed;
        }

        const {success, body} = await this.addNewRecord(params);
        if (success) {
          const resData = body.resultSetTemp[0];
          if (resData) {
            if (resData['uploadStatus'] === 'rejected') {
              this.$toasted.error(resData['validationErrors'].map(error => error).join(' '), {
                duration: TOASTER_DURATION,
              });
            } else {
              this.updateTodoListData(selectedUser);
              this.$toasted.success('Record updated successfully', {
                duration: TOASTER_DURATION,
              });
            }
          }
        } else {
          this.$toasted.error('Unable to update record', {
            duration: TOASTER_DURATION,
          });
        }
        this.loading = false;
      }
    },
    formatPostData(item) {
      const fieldMap = {};

      this.fields.forEach((field) => {
        fieldMap[field.name] = {
          name: field.name,
          isInternal: field.isInternal,
          isPrimaryKey: field.isPrimaryKey,
          isSuggested: false,
        };
      });

      const params = {};
      for (const name in item) {
        const field = fieldMap[name];
        if (field === undefined) {
          delete item[name];
          continue;
        } else if (field.isInternal && !field.isPrimaryKey) { //Skip auto populated columns since it throws error if it's passed
          continue;
        }
        params[name] = item[name];
      }

      return params;
    },
    async onEditClick(item) {
      const record = await this.getTableData(item); //record = ""
      const params = {
        inline: false,
        todo: true,
        record: record,
        entityUuid: item.entityGuid,
      };

      this.showAddNewRecordModal(params);
    },
    async getTableData(item) {
      await this.getTable({
        id: item.entityGuid,
        isTodo: true,
        relationalTableId: item.entityGuid,
      });

      return await this.getSingleRecordData({
        tableId: item.entityGuid,
        recordGuid: item.recordGuid,
      });
    },
    async onUserSelected(data) {
      await this.getTodoListByUser(data.userGuid);
    },
    async clearSelectedUser() {
      await this.getTodoListData();
    },
    async updateTodoListData(data) {
      if (data.name) {
        await this.onUserSelected(data);
      } else {
        await this.clearSelectedUser();
      }
    },
    contextChanged(context) {
      this.changePaginationData({perPage: context.pagination.perPage, currentPage: context.pagination.page});
    },
    onSortChanged(event) {
      this.sortedParams = {
        sortBy: event.sortBy,
        sortDesc: event.sortDesc,
      };
      if (!event.sortBy) {
        this.items = [...this.todoListData.data];
      } else {
        this.items.sort(function (a, b) {
          if (a[event.sortBy] < b[event.sortBy]) {
            return event.sortDesc ? 1 : -1;
          }
          if (a[event.sortBy] > b[event.sortBy]) {
            return event.sortDesc ? -1 : 1;
          }
          return 0;
        });
      }
    },
    filterData(e) {
      const filter = e.filter || e;
      this.filters = filter;
      this.items = this.todoListData.data.filter((item) => {
        for (const filterFieldName in filter) {
          if (!filter.hasOwnProperty(filterFieldName)) {
            continue;
          }
          const field = this.todoListFields.find((f) => f.key === filterFieldName);
          if (field.dataTypeName === this.fieldValues.text && !this.textFilter(filter[filterFieldName], item[filterFieldName])) {
            return false;
          } else if (field.dataTypeName === this.fieldValues.dateTime && !this.dateFilter(filter[filterFieldName], item[filterFieldName])) {
            return false;
          }
        }
        return true;
      });
    },
    async getRecordHistory(item) {
      await this.getTableFields(item.entityGuid);
      await this.getTable({
        id: item.entityGuid,
        pageNumber: 1,
        pageSize: 1,
      });
      await this.showViewRecordModal({
        entityGuid: item.entityGuid,
        recordGuid: item.recordGuid,
      });
    },
  },
  async mounted() {
    await this.getTodoListData();
    await this.getTodoUserList();
  },
};
</script>

<style lang="scss" scoped>

.complex-table-view-wrapper {
  flex: 1;
  overflow: hidden;
}
</style>
